import { useEffect, useRef } from 'react';
import { findSkin, type SkinDef, type ThemeDef } from '@snakefeast/shared';
import { arenaBorder, arenaGradient, drawSnake, themeTile, type Pt } from '../game/art';
import { loadArt } from '../game/foodArt';
import { prefersReducedMotion } from '../lib/platform';

/** A little S-shaped snake on a 5x5 grid, head first (heading up). */
const PATH: Array<[number, number]> = [[3, 1], [3, 2], [2, 2], [1, 2], [1, 3], [1, 4], [2, 4], [3, 4], [4, 4]];

function setup(c: HTMLCanvasElement, size: number) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  c.width = size * dpr;
  c.height = size * dpr;
  const g = c.getContext('2d')!;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { g, dpr };
}

/**
 * All animated previews share one loop at PREVIEW_FPS, and a preview only joins it while it's on
 * screen and the page isn't scrolling: Settings shows ~40 of them, and one requestAnimationFrame loop
 * each (offscreen ones too) made scrolling stutter on phones.
 */
const PREVIEW_FPS = 30;
const animating = new Set<(now: number) => void>();
let loop = 0;
let lastTick = 0;
/** Previews hold still while the page scrolls, so scrolling gets the whole frame. */
let scrolledAt = 0;
if (typeof window !== 'undefined') window.addEventListener('scroll', () => (scrolledAt = performance.now()), { capture: true, passive: true });
const tick = (now: number) => {
  loop = animating.size ? requestAnimationFrame(tick) : 0;
  if (now - lastTick < 1000 / PREVIEW_FPS - 2 || now - scrolledAt < 150) return;
  lastTick = now;
  for (const draw of animating) draw(now);
};
function animate(draw: (now: number) => void, on: boolean) {
  if (on) animating.add(draw);
  else animating.delete(draw);
  if (animating.size && !loop) loop = requestAnimationFrame(tick);
}

/** Cosmetic preview drawn with the in-game art (animated: flames, stars, blinking). */
export function SkinPreview({ skin, size = 64, animate: moving = true }: { skin: SkinDef; size?: number; animate?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const { g } = setup(c, size);
    const cs = size / 5.4;
    // A little low, so a crown or a diya on the head isn't cut off at the top.
    const pts: Pt[] = PATH.map(([x, y]) => [(x + 0.2) * cs + cs / 2, (y + 0.12) * cs + cs / 2]);
    const still = !moving || prefersReducedMotion();
    const draw = (now: number) => {
      g.clearRect(0, 0, size, size);
      // The pattern slides toward the head, so the skin looks like it's slithering.
      const flow = still ? 0 : (now / 1000) * 1.6;
      drawSnake(g, pts, { skin, cs, quality: 'high', reduceMotion: still, now, dir: 0, glow: skin.pattern === 'neon' || skin.pattern === 'flame', seed: skin.id.length, flow });
    };
    draw(0);
    if (still) return;
    const io = new IntersectionObserver(([e]) => animate(draw, !!e?.isIntersecting));
    io.observe(c);
    return () => {
      io.disconnect();
      animate(draw, false);
    };
  }, [skin, size, moving]);
  return <canvas ref={ref} aria-hidden="true" />;
}

/** A tiny arena in the theme: background, texture, border, a snake and some fruit. */
export function ThemePreview({ theme, size = 72 }: { theme: ThemeDef; size?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const { g, dpr } = setup(c, size);
    const cs = size / 7;
    g.fillStyle = arenaGradient(g, theme, size, size);
    g.fillRect(0, 0, size, size);
    const pat = g.createPattern(themeTile(theme, cs, dpr) as CanvasImageSource, 'repeat');
    if (pat) {
      pat.setTransform(new DOMMatrix().scale(1 / dpr));
      g.fillStyle = pat;
      g.fillRect(0, 0, size, size);
    }
    arenaBorder(g, theme, size, size, cs);
    const pts: Pt[] = [[4.5, 2.5], [4.5, 3.5], [3.5, 3.5], [2.5, 3.5], [2.5, 4.5]].map(([x, y]) => [x! * cs, y! * cs]);
    drawSnake(g, pts, { skin: findSkin('emerald'), cs, quality: 'high', reduceMotion: true, now: 0, dir: 0, glow: false });
    // The game's own fruit art, drawn once it has decoded (a moment, the first time).
    let live = true;
    void Promise.all([loadArt('apple'), loadArt('grapes')]).then(([apple, grapes]) => {
      if (!live) return;
      const box = cs * 1.3;
      if (apple) g.drawImage(apple, cs * 1.6 - box / 2, cs * 1.8 - box / 2, box, box);
      if (grapes) g.drawImage(grapes, cs * 5.4 - box / 2, cs * 5.6 - box / 2, box, box);
    });
    return () => {
      live = false;
    };
  }, [theme, size]);
  return <canvas ref={ref} aria-hidden="true" />;
}
